import { Link } from "react-router-dom";
import { Users, BookOpen, ClipboardList, ChevronRight, BarChart3, ShieldCheck } from "lucide-react";
import { useCmGetModeratorsQuery, useCmGetResultsQuery } from "../../api/chiefModeratorApi";

export default function ChiefModeratorDashboardPage() {
  const { data: moderators = [] } = useCmGetModeratorsQuery();
  const { data: all = [] }        = useCmGetResultsQuery();

  const pending   = (all as any[]).filter((r: any) => ["under_moderation", "moderation_rework"].includes(r.status)).length;
  const moderated = (all as any[]).filter((r: any) => r.status === "moderated").length;
  const published = (all as any[]).filter((r: any) => r.status === "published").length;

  const stats = [
    { label: "Moderators",     val: moderators.length, icon: Users,         color: "indigo"  },
    { label: "Pending Review", val: pending,            icon: ClipboardList, color: "amber"   },
    { label: "Moderated",      val: moderated,          icon: BarChart3,     color: "violet"  },
    { label: "Published",      val: published,          icon: BookOpen,      color: "emerald" },
  ];

  const nav = [
    {
      title: "Manage Moderators",
      desc:  `${moderators.length} moderator${moderators.length !== 1 ? "s" : ""} under supervision`,
      icon:  <Users className="w-6 h-6 text-indigo-500" />,
      href:  "/exams/chief-moderator/moderators",
      color: "indigo",
    },
    {
      title: "Assign Courses",
      desc:  "Map courses to moderators per diet",
      icon:  <BookOpen className="w-6 h-6 text-purple-500" />,
      href:  "/exams/chief-moderator/assign-courses",
      color: "purple",
    },
    {
      title: "Review Results",
      desc:  `${pending} batch${pending !== 1 ? "es" : ""} awaiting moderation`,
      icon:  <ClipboardList className="w-6 h-6 text-amber-500" />,
      href:  "/exams/chief-moderator/results",
      color: "amber",
      badge: pending,
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Chief Moderator</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">Manage moderators, course assignments, and moderate exam results.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {nav.map((c) => (
          <Link key={c.href} to={c.href}
            className="relative bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all group flex flex-col gap-4">
            {(c.badge ?? 0) > 0 && (
              <span className="absolute top-4 right-4 min-w-[1.25rem] h-5 px-1.5 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {c.badge}
              </span>
            )}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${c.color}-50`}>
              {c.icon}
            </div>
            <div>
              <p className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{c.title}</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{c.desc}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-indigo-400 transition-colors self-end mt-auto" />
          </Link>
        ))}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-violet-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-violet-500/20">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-violet-400 mb-2">Moderation Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Approved batches are assigned to moderators for review. After moderation, you review their feedback and
            forward any rework instructions to the Chief Examiner, who then passes them to the examiner.
          </p>
        </div>
      </div>
    </div>
  );
}
